import React, { useState, useEffect } from 'react';
import { apiFetch, setToken } from '../api';

// ── Design tokens ─────────────────────────────────────────────────────────────

const T = {
  bg:         '#f4ecd9',
  paper:      '#fdf6e3',
  paperHigh:  '#fffaf0',
  ink:        '#22180e',
  inkSoft:    '#6a5a3e',
  inkMute:    '#a89570',
  accent:     '#c75a2c',
  accentSoft: '#f8dcc1',
  teal:       '#4e8a90',
  tealSoft:   '#cce0e2',
  divider:    'rgba(34,24,14,0.08)',
  display:    "'Newsreader', Georgia, serif",
  body:       "'Work Sans', system-ui, sans-serif",
  radius:     18,
  radiusLg:   28,
};

// ── Icons ─────────────────────────────────────────────────────────────────────

function Icon({ name, size = 20, stroke = 1.5, color = 'currentColor' }) {
  const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: stroke, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'shield': return <svg {...p}><path d="M12 3l8 3v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-3z" /><path d="M9 12l2 2 4-4" /></svg>;
    case 'check':  return <svg {...p}><path d="M4 12.5l5 5L20 6.5" /></svg>;
    case 'arrowRight': return <svg {...p}><path d="M5 12h14M13 6l6 6-6 6" /></svg>;
    default: return null;
  }
}

// ── Progress dots ─────────────────────────────────────────────────────────────

function ProgressDots({ step, total = 5 }) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 40 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          height: 4, borderRadius: 2,
          width: i === step ? 24 : 8,
          background: i <= step ? T.accent : T.divider,
          transition: 'all 0.3s ease',
        }} />
      ))}
    </div>
  );
}

// ── Shared input style ────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%',
  background: T.paper,
  border: `1.5px solid ${T.divider}`,
  borderRadius: T.radius,
  padding: '16px 18px',
  fontSize: 18,
  fontFamily: T.body,
  color: T.ink,
  boxSizing: 'border-box',
  outline: 'none',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};

const btnStyle = {
  width: '100%', minHeight: 56,
  background: T.ink,
  border: 'none', borderRadius: T.radiusLg,
  fontFamily: T.body, fontSize: 17, fontWeight: 600,
  color: T.paper, cursor: 'pointer',
  transition: 'opacity 0.15s, transform 0.1s',
  letterSpacing: 0.1,
};

// ── Step 1 — Welcome ──────────────────────────────────────────────────────────

function StepWelcome({ invite, onNext }) {
  return (
    <div style={{ textAlign: 'center' }}>
      {/* Envelope motif */}
      <div style={{ marginBottom: 32, position: 'relative', display: 'inline-block' }}>
        <div style={{
          width: 88, height: 88,
          background: T.accentSoft,
          borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 40,
          boxShadow: `0 8px 32px -8px rgba(199,90,44,0.30)`,
        }}>
          ✉️
        </div>
        {/* Wax seal */}
        <div style={{
          position: 'absolute', bottom: -4, right: -4,
          width: 28, height: 28, borderRadius: '50%',
          background: T.accent,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `2px solid ${T.paper}`,
          fontSize: 12,
        }}>
          <Icon name="check" size={14} color={T.paper} />
        </div>
      </div>

      <p style={{ fontFamily: T.body, fontSize: 12, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: T.inkMute, margin: '0 0 10px' }}>
        You're invited
      </p>

      <h1 style={{ fontFamily: T.display, fontSize: 34, fontWeight: 400, letterSpacing: -0.4, lineHeight: 1.1, color: T.ink, margin: '0 0 14px' }}>
        {invite?.group_name
          ? <><em style={{ color: T.accent }}>{invite.group_name}</em>{' '}would like you to join.</>
          : <>Your family would like you to <em style={{ color: T.accent }}>join.</em></>
        }
      </h1>

      <p style={{ fontFamily: T.body, fontSize: 17, color: T.inkSoft, lineHeight: 1.6, margin: '0 0 36px' }}>
        You'll have your own space to record and listen to family stories — and it takes less than a minute to get started.
      </p>

      <button style={btnStyle} onClick={onNext}>
        Let's get started
      </button>

      <p style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute, marginTop: 16 }}>
        Joining as a{' '}
        <strong style={{ color: T.inkSoft, textTransform: 'capitalize' }}>{invite?.role || 'member'}</strong>
      </p>
    </div>
  );
}

// ── Step 2 — Name ─────────────────────────────────────────────────────────────

function StepName({ name, setName, onNext, error }) {
  return (
    <div>
      <p style={{ fontFamily: T.body, fontSize: 12, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: T.inkMute, margin: '0 0 10px', textAlign: 'center' }}>
        Step 1 of 3
      </p>
      <h1 style={{ fontFamily: T.display, fontSize: 34, fontWeight: 400, letterSpacing: -0.4, lineHeight: 1.1, color: T.ink, margin: '0 0 10px', textAlign: 'center' }}>
        What should we <em style={{ color: T.accent }}>call you?</em>
      </h1>
      <p style={{ fontFamily: T.body, fontSize: 16, color: T.inkSoft, textAlign: 'center', margin: '0 0 32px', lineHeight: 1.55 }}>
        Use whatever name your family calls you.
      </p>

      <input
        className="memories-join-input"
        style={inputStyle}
        placeholder="Your name"
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && name.trim() && onNext()}
        autoFocus
        autoComplete="name"
      />

      {error && <div style={{ marginTop: 10, background: '#fdf3e8', border: `1px solid ${T.accentSoft}`, borderRadius: 12, padding: '12px 14px', fontFamily: T.body, fontSize: 14, color: T.inkSoft }}>{error}</div>}

      <button style={{ ...btnStyle, marginTop: 16, opacity: name.trim() ? 1 : 0.5 }} onClick={onNext} disabled={!name.trim()}>
        Continue
      </button>
    </div>
  );
}

// ── Step 3 — Email ────────────────────────────────────────────────────────────

function StepEmail({ email, setEmail, onNext, error }) {
  return (
    <div>
      <p style={{ fontFamily: T.body, fontSize: 12, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: T.inkMute, margin: '0 0 10px', textAlign: 'center' }}>
        Step 2 of 3
      </p>
      <h1 style={{ fontFamily: T.display, fontSize: 34, fontWeight: 400, letterSpacing: -0.4, lineHeight: 1.1, color: T.ink, margin: '0 0 10px', textAlign: 'center' }}>
        Your <em style={{ color: T.accent }}>email address.</em>
      </h1>
      <p style={{ fontFamily: T.body, fontSize: 16, color: T.inkSoft, textAlign: 'center', margin: '0 0 20px', lineHeight: 1.55 }}>
        So we know it's you each time you sign in.
      </p>

      {/* Privacy pill */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.tealSoft, border: `1px solid ${T.teal}22`, borderRadius: 999, padding: '9px 14px', marginBottom: 20, justifyContent: 'center' }}>
        <Icon name="shield" size={15} stroke={1.5} color={T.teal} />
        <span style={{ fontFamily: T.body, fontSize: 13, color: T.teal, fontWeight: 500 }}>Only your family can see your stories.</span>
      </div>

      <input
        className="memories-join-input"
        style={inputStyle}
        type="email"
        placeholder="your@email.com"
        value={email}
        onChange={e => setEmail(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && email.trim() && onNext()}
        autoFocus
        autoComplete="email"
        autoCapitalize="none"
      />

      {error && <div style={{ marginTop: 10, background: '#fdf3e8', border: `1px solid ${T.accentSoft}`, borderRadius: 12, padding: '12px 14px', fontFamily: T.body, fontSize: 14, color: T.inkSoft }}>{error}</div>}

      <button style={{ ...btnStyle, marginTop: 16, opacity: email.trim() ? 1 : 0.5 }} onClick={onNext} disabled={!email.trim()}>
        Continue
      </button>
    </div>
  );
}

// ── Step 4 — Phone recognition ────────────────────────────────────────────────

function StepPasskey({ onNext, loading, error }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontFamily: T.body, fontSize: 12, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: T.inkMute, margin: '0 0 10px' }}>
        Step 3 of 3
      </p>

      {/* Face ID glyph */}
      <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'center' }}>
        <div style={{
          width: 72, height: 72,
          background: T.accentSoft,
          borderRadius: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: loading ? 'faceIDPulse 1.8s ease-in-out infinite' : 'none',
          boxShadow: '0 4px 16px -4px rgba(199,90,44,0.25)',
        }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
            stroke={T.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4.5C4 3.7 4.7 3 5.5 3H11v17H5.5C4.7 20 4 19.3 4 18.5v-14z" />
            <path d="M20 4.5C20 3.7 19.3 3 18.5 3H13v17h5.5c.8 0 1.5-.7 1.5-1.5v-14z" />
            <path d="M11 3v17M13 3v17" />
          </svg>
        </div>
      </div>

      <h1 style={{ fontFamily: T.display, fontSize: 30, fontWeight: 400, letterSpacing: -0.4, lineHeight: 1.15, color: T.ink, margin: '0 0 14px' }}>
        {loading
          ? <>Your phone will <em style={{ color: T.accent }}>recognize you.</em></>
          : <>One last <em style={{ color: T.accent }}>step.</em></>
        }
      </h1>

      <p style={{ fontFamily: T.body, fontSize: 16, color: T.inkSoft, lineHeight: 1.6, margin: '0 0 32px' }}>
        {loading
          ? 'Follow the prompt — the same way you unlock your phone.'
          : 'Your phone will ask to recognize you. It works the same way you unlock your phone — simple and safe.'
        }
      </p>

      {error && <div style={{ marginBottom: 16, background: '#fdf3e8', border: `1px solid ${T.accentSoft}`, borderRadius: 12, padding: '12px 14px', fontFamily: T.body, fontSize: 14, color: T.inkSoft, textAlign: 'left' }}>{error}</div>}

      <button style={{ ...btnStyle, opacity: loading ? 0.6 : 1 }} onClick={onNext} disabled={loading}>
        {loading ? 'Setting up…' : 'Set up my account'}
      </button>

      <p style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute, marginTop: 16, lineHeight: 1.5 }}>
        No password is ever stored. Your phone's built-in security keeps your account safe.
      </p>
    </div>
  );
}

// ── Step 5 — Welcome ──────────────────────────────────────────────────────────

function StepDone({ name }) {
  return (
    <div style={{ textAlign: 'center' }}>
      {/* Success medallion */}
      <div style={{ marginBottom: 32, display: 'flex', justifyContent: 'center', position: 'relative' }}>
        <div style={{ width: 96, height: 96, borderRadius: '50%', background: T.tealSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px -6px rgba(199,90,44,0.45)' }}>
            <Icon name="check" size={32} stroke={2} color={T.paper} />
          </div>
        </div>
      </div>

      <p style={{ fontFamily: T.body, fontSize: 12, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: T.inkMute, margin: '0 0 10px' }}>
        You're in
      </p>

      <h1 style={{ fontFamily: T.display, fontSize: 36, fontWeight: 400, letterSpacing: -0.4, lineHeight: 1.1, color: T.ink, margin: '0 0 14px' }}>
        Welcome to the <em style={{ color: T.accent }}>family.</em>
      </h1>

      <p style={{ fontFamily: T.body, fontSize: 17, color: T.inkSoft, lineHeight: 1.6, margin: 0 }}>
        {name ? `Hello, ${name.split(' ')[0]}.` : 'Hello.'} Your family album is ready for you.
      </p>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function JoinScreen({ token, onComplete }) {
  const [step, setStep] = useState(-1); // -1=loading, 0=welcome, 1=name, 2=email, 3=passkey, 4=done
  const [invite, setInvite] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    // Load fonts
    if (!document.getElementById('memories-fonts')) {
      const link = document.createElement('link');
      link.id = 'memories-fonts';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,300..700;1,6..72,300..700&family=Work+Sans:wght@300..700&display=swap';
      document.head.appendChild(link);
    }
    document.body.style.background = T.bg;

    apiFetch('/admin/invite/' + token)
      .then(data => { setInvite(data); setStep(0); })
      .catch(() => setStep(-2)); // -2 = invalid
  }, [token]);

  const register = async () => {
    setRegistering(true); setError('');
    try {
      const startRes = await apiFetch('/auth/register/start', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), email: email.trim(), invite_token: token }),
      });

      const credential = await navigator.credentials.create({ publicKey: startRes.options });

      const finishRes = await apiFetch('/auth/register/finish', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          invite_token: token,
          credential: {
            id: credential.id,
            rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))),
            type: credential.type,
            response: {
              clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(credential.response.clientDataJSON))),
              attestationObject: btoa(String.fromCharCode(...new Uint8Array(credential.response.attestationObject))),
            },
          },
        }),
      });

      setToken(finishRes.token);
      setStep(4);
      setTimeout(() => onComplete(finishRes.user), 2000);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again or ask for a new invite link.');
      setRegistering(false);
    }
  };

  const next = () => { setError(''); setStep(s => s + 1); };

  const validateName = () => {
    if (!name.trim()) { setError('Please enter your name.'); return; }
    next();
  };

  const validateEmail = () => {
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    if (!email.includes('@')) { setError('Please enter a valid email address.'); return; }
    next();
  };

  const startPasskey = () => {
    setStep(3);
    register();
  };

  return (
    <>
      <style>{`
        @keyframes faceIDPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%       { transform: scale(1.04); opacity: 0.85; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .memories-join-input:focus {
          border-color: ${T.accent} !important;
          box-shadow: 0 0 0 3px ${T.accentSoft} !important;
        }
      `}</style>

      <div style={{
        minHeight: '100vh', background: T.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, fontFamily: T.body,
      }}>
        <div style={{
          width: '100%', maxWidth: 360,
          animation: 'fadeUp 0.35s ease',
        }}>

          {/* Wordmark */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <span style={{ fontFamily: T.display, fontSize: 26, fontWeight: 400, letterSpacing: -0.4, color: T.ink, fontStyle: 'italic' }}>
              memories
            </span>
            <span style={{ fontFamily: T.display, fontSize: 26, color: T.accent }}>.</span>
          </div>

          {/* Loading */}
          {step === -1 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 36, height: 36, border: `2px solid ${T.divider}`, borderTop: `2px solid ${T.accent}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
              <p style={{ fontFamily: T.body, fontSize: 16, color: T.inkSoft }}>Checking your invite…</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* Invalid */}
          {step === -2 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 20 }}>📭</div>
              <h1 style={{ fontFamily: T.display, fontSize: 28, fontWeight: 400, color: T.ink, margin: '0 0 12px' }}>
                This invite has <em style={{ color: T.accent }}>expired.</em>
              </h1>
              <p style={{ fontFamily: T.body, fontSize: 16, color: T.inkSoft, lineHeight: 1.6 }}>
                This link is no longer valid — it may have expired or already been used. Ask your family admin to send a new one.
              </p>
            </div>
          )}

          {/* Progress dots — only for active steps */}
          {step >= 0 && step <= 4 && <ProgressDots step={step} total={5} />}

          {/* Steps */}
          {step === 0 && <StepWelcome invite={invite} onNext={next} />}
          {step === 1 && <StepName name={name} setName={setName} onNext={validateName} error={error} />}
          {step === 2 && <StepEmail email={email} setEmail={setEmail} onNext={validateEmail} error={error} />}
          {step === 3 && <StepPasskey onNext={startPasskey} loading={registering} error={error} />}
          {step === 4 && <StepDone name={name} />}

        </div>
      </div>
    </>
  );
}
